'use client';

import * as React from 'react';
import { CheckIcon, ChevronDownIcon, CircleDollarSign, Store } from 'lucide-react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SETUP_SOFT_INPUT_CLASS } from '@/components/setup/experience/SetupStackedFormField';
import { SetupStoreFormRow } from '@/components/setup/experience/SetupStoreFormRow';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  SETUP_MANUAL_CURRENCIES,
  type SetupManualStoreFormData,
} from '@/components/setup/setupManualSchemas';

export function SetupStoreNameField() {
  const { t } = useTranslation('setup');
  const {
    control,
    formState: { errors },
  } = useFormContext<SetupManualStoreFormData>();
  const errorMessage = errors.name?.message;

  return (
    <SetupStoreFormRow
      label={t('experience.connect.storeForm.nameLabel')}
      htmlFor="setup-store-name"
      required
      icon={Store}
      hint={t('experience.connect.storeForm.nameHint')}
      error={errorMessage}
    >
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <Input
            id="setup-store-name"
            {...field}
            value={field.value || ''}
            placeholder={t('experience.connect.storeForm.namePlaceholder')}
            className={cn(SETUP_SOFT_INPUT_CLASS, 'h-11 px-4 text-[0.9375rem]')}
            autoComplete="organization"
            aria-required
            aria-invalid={!!errorMessage}
            aria-describedby={errorMessage ? 'setup-store-name-error' : undefined}
          />
        )}
      />
    </SetupStoreFormRow>
  );
}

function formatCurrencyOption(code: string, locale: string): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'currency' }).of(code);
    return name && name !== code ? `${code} — ${name}` : code;
  } catch {
    return code;
  }
}

export function SetupStoreCurrencyField() {
  const { t, i18n } = useTranslation(['setup', 'common']);
  const {
    control,
    formState: { errors },
  } = useFormContext<SetupManualStoreFormData>();
  const [open, setOpen] = React.useState(false);

  return (
    <SetupStoreFormRow
      label={t('experience.connect.storeForm.currencyLabel')}
      htmlFor="setup-store-currency"
      icon={CircleDollarSign}
      hint={t('experience.connect.storeForm.currencyHint')}
      error={errors.currency?.message}
    >
      <Controller
        name="currency"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                id="setup-store-currency"
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                  SETUP_SOFT_INPUT_CLASS,
                  'h-11 justify-between px-4 text-[0.9375rem] font-normal',
                  !value && 'text-muted-foreground',
                )}
              >
                <span className="truncate">
                  {value
                    ? formatCurrencyOption(value, i18n.language)
                    : t('common:currencySelect.placeholder')}
                </span>
                <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandInput placeholder={t('common:currencySelect.searchPlaceholder')} />
                <CommandList>
                  <CommandEmpty>{t('common:currencySelect.noCurrencyFound')}</CommandEmpty>
                  <CommandGroup>
                    {SETUP_MANUAL_CURRENCIES.map((code) => {
                      const label = formatCurrencyOption(code, i18n.language);
                      const isSelected = value === code;
                      return (
                        <CommandItem
                          key={code}
                          value={label}
                          onSelect={() => {
                            onChange(code);
                            setOpen(false);
                          }}
                        >
                          <CheckIcon
                            className={cn('mr-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')}
                          />
                          <span>{label}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      />
    </SetupStoreFormRow>
  );
}
